"use client";

import { type ShareOptions, shareLink } from "@/lib/share";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

export interface UseShareLinkOptions extends Partial<ShareOptions> {
  /** Message shown when link is copied to clipboard */
  copySuccessMessage?: string;
  /** Message shown when clipboard copy fails */
  copyErrorMessage?: string;
  /** When Web Share fails for reasons other than user cancel */
  shareErrorMessage?: string;
  /** When Web Share and clipboard are both unavailable (e.g. non-HTTPS) */
  shareUnavailableMessage?: string;
}

const DEFAULT_SHARE_UNAVAILABLE_MESSAGE =
  "Sharing isn't available here—try HTTPS or copy the link from the address bar";

const DEFAULT_SHARE_ERROR_MESSAGE = "Couldn’t open share—try copying the link from the address bar";

const NO_LINK_TO_SHARE_MESSAGE = "No link to share";

function isShareableUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  try {
    const u = new URL(trimmed);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Hook that returns a share handler. Uses navigator.share when available,
 * falls back to clipboard. Shows toast on success/failure.
 * When url is provided in options, share() can be called with no args.
 */
export function useShareLink(options: UseShareLinkOptions = {}) {
  const {
    url: urlOpt,
    title: titleOpt,
    text: textOpt,
    copySuccessMessage = "Link copied to clipboard",
    copyErrorMessage = "Failed to copy link",
    shareErrorMessage = DEFAULT_SHARE_ERROR_MESSAGE,
    shareUnavailableMessage = DEFAULT_SHARE_UNAVAILABLE_MESSAGE,
  } = options;

  const canShare = useMemo(() => isShareableUrl(urlOpt ?? ""), [urlOpt]);

  const [isSharing, setIsSharing] = useState(false);

  const doShare = useCallback(
    async (opts: ShareOptions) => {
      setIsSharing(true);
      try {
        const result = await shareLink(opts);
        if (result.success) {
          if (result.method === "clipboard") {
            toast.success(copySuccessMessage);
          }
          return true;
        }
        // User dismissed native share sheet — not a failure worth surfacing
        if (
          result.method === "share" &&
          result.error instanceof Error &&
          result.error.name === "AbortError"
        ) {
          return false;
        }
        const isClipboardUnsupported =
          result.method === "clipboard" &&
          result.error instanceof Error &&
          result.error.name === "NotSupportedError";
        const isShareFailure = result.method === "share";
        const message = isClipboardUnsupported
          ? shareUnavailableMessage
          : isShareFailure
            ? shareErrorMessage
            : copyErrorMessage;
        toast.error(message);
        return false;
      } finally {
        setIsSharing(false);
      }
    },
    [copySuccessMessage, copyErrorMessage, shareErrorMessage, shareUnavailableMessage]
  );

  const share = useCallback(
    (opts?: Partial<ShareOptions>) => {
      const url = (opts?.url ?? urlOpt ?? "").trim();
      const title = opts?.title ?? titleOpt ?? "";
      const text = opts?.text ?? textOpt ?? "";
      if (!url) {
        toast.error(NO_LINK_TO_SHARE_MESSAGE);
        return Promise.resolve(false);
      }
      if (!isShareableUrl(url)) {
        toast.error(NO_LINK_TO_SHARE_MESSAGE);
        return Promise.resolve(false);
      }
      return doShare({ url, title, text });
    },
    [doShare, urlOpt, titleOpt, textOpt]
  );

  return { share, isSharing, canShare };
}
